'use client';

import { type Dispatch, forwardRef, type SetStateAction } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Image } from '@packages/shared/src/ui/Image/Image';
import type { NavigationItem } from '../navigationTypes';
import { ResponsiveFlyoutItem } from './ResponsiveFlyoutItem';

const messages = defineMessages({
  currentCategoryLink: {
    id: 'header.navigation.flyout.currentCategoryLink',
    defaultMessage: 'Alles in {categoryName}',
  },
  moreCategories: {
    id: 'header.navigation.flyout.moreCategories',
    defaultMessage: 'Mehr ...',
  },
  secondaryNavigationCategoryList: {
    id: 'secondary.navigation.categories.list.label',
    defaultMessage: 'Unterkategorien',
  },
});

type ResponsiveFlyoutContentProps = {
  item?: NavigationItem;
  onItemClick?: (item: NavigationItem) => void;
  onNavigate?: (item: NavigationItem) => void;
  onItem?: Dispatch<SetStateAction<boolean>>;
};

/**
 * Content for the navigation popup, displaying child items of the currently selected category.
 */

export const ResponsiveFlyoutContent = forwardRef<HTMLUListElement, ResponsiveFlyoutContentProps>(
  ({ item, onItemClick, onNavigate, onItem }, ref) => {
    const { formatMessage } = useIntl();
    const hasSelfLink = item?.uri !== undefined;

    const tooManyItems = (item?.children?.length ?? 0) > (hasSelfLink ? 24 : 25);

    const maxItems = tooManyItems && hasSelfLink ? 23 : 25;

    return (
      <div>
        {item?.image && (
          <div
            className={`${item?.level === 1 ? 'block' : 'hidden'} bg-color-grey-light relative h-[8rem] self-stretch overflow-hidden lg:hidden`}
          >
            <Image
              src={item.image}
              // decorative image in line with WAI standards
              alt=""
              fill
              style={{ objectFit: 'contain', width: '100%', height: '100%' }}
            />
          </div>
        )}
        <ul
          className="m-0 list-none items-stretch p-0 max-lg:flex max-lg:flex-col lg:grid lg:grid-flow-col lg:[grid-template-columns:repeat(5,1fr)] lg:[grid-template-rows:repeat(5,auto)] lg:gap-1.5"
          ref={ref}
          aria-label={formatMessage(messages.secondaryNavigationCategoryList)}
        >
          {hasSelfLink && (
            <ResponsiveFlyoutItem
              href={item.uri}
              allowNavigation
              onClick={() => {
                onItem?.(true);
                onItemClick?.(item);
              }}
              onNavigate={() => onNavigate?.(item)}
            >
              {formatMessage(messages.currentCategoryLink, { categoryName: item.displayName })}
            </ResponsiveFlyoutItem>
          )}
          {item?.children?.slice(0, maxItems)?.map((child) => (
            <ResponsiveFlyoutItem
              key={child.uri}
              href={child.uri}
              allowNavigation={!!child.uri && !child.children.length}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  if (!!child.uri && !child.children.length) {
                    onNavigate?.(child);
                  } else {
                    event.preventDefault();
                    onItem?.(true);
                    onItemClick?.(child);
                  }
                }
              }}
              onClick={() => {
                onItem?.(true);
                onItemClick?.(child);
              }}
              onNavigate={() => onNavigate?.(child)}
            >
              {child.displayName}
            </ResponsiveFlyoutItem>
          ))}
          {tooManyItems && hasSelfLink && (
            <ResponsiveFlyoutItem
              href={item.uri}
              allowNavigation
              onClick={() => {
                onItem?.(true);
                onItemClick?.(item);
              }}
              onNavigate={() => onNavigate?.(item)}
            >
              {formatMessage(messages.moreCategories)}
            </ResponsiveFlyoutItem>
          )}
        </ul>
      </div>
    );
  },
);
